import './SettingsModal.css'
import type { LoginResponse } from '../shared/types/LoginResponse'
import type { AiModel } from '../shared/types/AiModel'
import { DEFAULT_AI_MODEL } from '../shared/types/AiModel'
import { useEffect, useState, useMemo } from 'react'
import { container } from 'tsyringe'
import { ApiService } from '../api/ApiService'
import { BodyBlocker } from './BodyBlocker'
import { BuyTokensModal } from './BuyTokensModal'
import { useTranslation } from 'react-i18next'
import { NotifyModal } from './NotifyModal'

type AiModelDisplay = 'Free' | 'Premium' | 'Balanced'
const apiToDisplay: Record<string, AiModelDisplay> = {
  gpt: 'Free',
  llama: 'Premium',
  balanced: 'Balanced',
}
const displayToApi: Record<AiModelDisplay, AiModel> = {
  Free: 'gpt',
  Premium: 'llama',
  Balanced: 'balanced',
}

export function SettingsModal({
  data,
  onClose,
  onSave,
}: {
  data: LoginResponse
  onClose: () => void
  onSave: (info: LoginResponse) => void
}) {
  const { user } = data
  const api = useMemo(() => container.resolve(ApiService), [])
  const [tokens, setTokens] = useState<number>(0)
  const [showBuyTokensModal, setShowBuyTokensModal] = useState(false)
  const [language, setLanguage] = useState<string>(user.preferredLanguage ?? 'en')
  const [aiModel, setAiModel] = useState<AiModelDisplay>(
    user.preferredAiModel
      ? apiToDisplay[user.preferredAiModel] ?? apiToDisplay[DEFAULT_AI_MODEL]
      : apiToDisplay[DEFAULT_AI_MODEL],
  )
  const [aiModels, setAiModels] = useState<AiModelDisplay[]>([])
  const [notifyText, setNotifyText] = useState<string | null>(null)
  const [phoneNumber, setPhoneNumber] = useState('')
  const { t } = useTranslation()

  const formatDate = (iso: string | null) => {
    if (!iso) return ''
    const date = new Date(iso)
    return date.toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  }

  useEffect(() => {
    api
      .getTokenBalance()
      .then(setTokens)
      .catch(() => setTokens(0))
  }, [api])

  useEffect(() => {
    api
      .getAiModels()
      .then(list => {
        const mapped = list
          .map(m => apiToDisplay[m])
          .filter((m): m is AiModelDisplay => m !== undefined)
        const defaultDisplay = apiToDisplay[DEFAULT_AI_MODEL]
        setAiModels(mapped)
        if (!user.preferredAiModel && mapped.length > 0)
          setAiModel(
            mapped.includes(defaultDisplay) ? defaultDisplay : mapped[0],
          )
      })
      .catch(() => setAiModels([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.preferredAiModel])

  const handleBuyTokens = () => {
    setShowBuyTokensModal(true)
  }

  const handlePremiumChanged = (info: { premiumLevel: number; premiumUntil: string }) => {
    const updated: LoginResponse = {
      ...data,
      user: {
        ...data.user,
        preferredLanguage: language,
        preferredAiModel: displayToApi[aiModel],
      },
      premiumLevel: info.premiumLevel,
      premiumUntil: info.premiumUntil,
    }
    onSave(updated)
  }

  const premiumUntilDate = data.premiumUntil ? new Date(data.premiumUntil) : null
  const premiumActive =
    data.premiumLevel > 0 && !!premiumUntilDate && premiumUntilDate > new Date()
  const premiumName = premiumActive
    ? data.premiumLevel === 2
      ? t('gleam_x')
      : t('gleam_premium')
    : null
  const premiumDate = premiumActive && data.premiumUntil ? formatDate(data.premiumUntil) : ''
  const premiumCaption = premiumName ?? t('settings_premium')
  const premiumValue = premiumActive
    ? premiumDate
      ? `${t('settings_until')} ${premiumDate}`
      : t('settings_until')
    : t('not_specified')

  const ИконкаПользователя = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5 4C5 3.20435 5.31607 2.44129 5.87868 1.87868C6.44129 1.31607 7.20435 1 8 1C8.79565 1 9.55871 1.31607 10.1213 1.87868C10.6839 2.44129 11 3.20435 11 4C11 4.79565 10.6839 5.55871 10.1213 6.12132C9.55871 6.68393 8.79565 7 8 7C7.20435 7 6.44129 6.68393 5.87868 6.12132C5.31607 5.55871 5 4.79565 5 4ZM2.50067 13.4033C2.52315 11.9596 3.11243 10.5827 4.14131 9.56972C5.17019 8.55674 6.55614 7.98897 8 7.98897C9.44386 7.98897 10.8298 8.55674 11.8587 9.56972C12.8876 10.5827 13.4769 11.9596 13.4993 13.4033C13.5011 13.5005 13.4744 13.5961 13.4227 13.6784C13.3709 13.7607 13.2963 13.8261 13.208 13.8667C11.5741 14.6158 9.79744 15.0024 8 15C6.14267 15 4.378 14.5947 2.792 13.8667C2.70366 13.8261 2.62905 13.7607 2.57731 13.6784C2.52557 13.5961 2.49893 13.5005 2.50067 13.4033Z"
        fill="#FF0782"
      />
    </svg>
  )

  const ИконкаТокенов = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M14 4.25C14 6.04467 11.3133 7.5 8 7.5C4.68667 7.5 2 6.04467 2 4.25C2 2.45533 4.68667 1 8 1C11.3133 1 14 2.45533 14 4.25Z" fill="#FF0782" />
      <path d="M8 8.49997C9.79 8.49997 11.46 8.10931 12.7187 7.42731C13.1819 7.18137 13.6077 6.87069 13.9833 6.50464C13.994 6.58531 14 6.66731 14 6.74997C14 8.54464 11.3133 9.99997 8 9.99997C4.68667 9.99997 2 8.54464 2 6.74997C2 6.66731 2.006 6.58531 2.01667 6.50464C2.39228 6.87068 2.81811 7.18135 3.28133 7.42731C4.53933 8.10931 6.21 8.49997 8 8.49997Z" fill="#FF0782" />
      <path d="M8 11C9.79 11 11.46 10.6093 12.7187 9.92731C13.1819 9.68138 13.6077 9.3707 13.9833 9.00464C13.994 9.08531 14 9.16731 14 9.24997C14 11.0446 11.3133 12.5 8 12.5C4.68667 12.5 2 11.0446 2 9.24997C2 9.16731 2.006 9.08531 2.01667 9.00464C2.39227 9.37068 2.8181 9.68136 3.28133 9.92731C4.53933 10.6093 6.21 11 8 11Z" fill="#FF0782" />
      <path d="M8 13.5C9.79 13.5 11.46 13.1093 12.7187 12.4273C13.1819 12.1814 13.6077 11.8707 13.9833 11.5046C13.994 11.5853 14 11.6673 14 11.75C14 13.5446 11.3133 15 8 15C4.68667 15 2 13.5446 2 11.75C2 11.6673 2.006 11.5853 2.01667 11.5046C2.39227 11.8707 2.8181 12.1814 3.28133 12.4273C4.53933 13.1093 6.21 13.5 8 13.5Z" fill="#FF0782" />
    </svg>
  )

  const ИконкаПремиума = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.19201 2.13994C7.49068 1.42194 8.50935 1.42194 8.80801 2.13994L10.196 5.47727L13.7987 5.76661C14.5747 5.82861 14.8893 6.79661 14.298 7.30327L11.5533 9.65461L12.3913 13.1699C12.572 13.9273 11.7487 14.5253 11.0847 14.1199L8.00001 12.2359L4.91534 14.1199C4.25134 14.5253 3.42801 13.9266 3.60868 13.1699L4.44668 9.65461L1.70201 7.30327C1.11068 6.79661 1.42535 5.82861 2.20135 5.76661L5.80401 5.47727L7.19201 2.13994Z"
        fill="#FF0782"
      />
    </svg>
  )

  return (
    <div className="settings-backdrop" onClick={onClose}>
      <div className="settings-window" onClick={e => e.stopPropagation()}>
        <BodyBlocker />
        <div className="settings-header">
          <h2>{t('settings')}</h2>
        </div>

        <div className="settings-top-grid">
          <div className="settings-top-card">
            <div className="settings-top-icon"><ИконкаПользователя /></div>
            <div className="settings-top-caption">{t('settings_user')}</div>
            <div className="settings-top-value">@{user.username}</div>
          </div>
          <div className="settings-top-card">
            <div className="settings-top-icon"><ИконкаТокенов /></div>
            <div className="settings-top-caption">{t('settings_tokens')}</div>
            <div className="settings-top-value">{tokens}</div>
          </div>
          <div className="settings-top-card">
            <div className="settings-top-icon"><ИконкаПремиума /></div>
            <div className="settings-top-caption">{premiumCaption}</div>
            <div className="settings-top-value">{premiumValue}</div>
          </div>
        </div>

        <button className="settings-buy" type="button" onPointerUp={handleBuyTokens}>
          {t('settings_buy_tokens')}
        </button>

        <div className="settings-section">
          <h3>{t('preferred_language')}</h3>
          <div className="settings-toggle-group">
            <button
              type="button"
              className={`settings-toggle ${language === 'en' ? 'active' : ''}`}
              onPointerUp={() => setLanguage('en')}
            >
              {t('settings_language_en')}
            </button>
            <button
              type="button"
              className={`settings-toggle ${language === 'ru' ? 'active' : ''}`}
              onPointerUp={() => setLanguage('ru')}
            >
              {t('settings_language_ru')}
            </button>
          </div>
        </div>

        <div className="settings-section">
          <h3>{t('phone_number')}</h3>
          <div className="settings-card settings-phone-card">
            <input
              type="tel"
              className="settings-text-input"
              placeholder="+7 (912) 345-67-89"
              value={phoneNumber}
              onChange={e => setPhoneNumber(e.target.value)}
            />
          </div>
        </div>

        <div className="settings-section">
          <h3>{t('preferred_ai_model')}</h3>
          <div className="settings-select">
            <select
              value={aiModel}
              onChange={(e) => setAiModel(e.target.value as AiModelDisplay)}
            >
              {aiModels
                .filter((m): m is AiModelDisplay => m !== undefined)
                .map((m) => (
                  <option key={m} value={m}>
                    {t(`ai_model_${displayToApi[m]}`)}
                  </option>
                ))}
            </select>
          </div>
        </div>

        <div className="settings-section">
          <h3>{t('site_account')}</h3>
          <div className="settings-card">
            <span>{t('not_linked')}</span>
            <button type="button" disabled>{t('settings_link')}</button>
          </div>
        </div>

        <div className="settings-section">
          <h3>{t('your_preferences')}</h3>
          <div className="settings-card">
            <span>{t('what_model_should_know')}</span>
            <button type="button" disabled>{t('settings_edit')}</button>
          </div>
        </div>

        <div className="settings-actions">
          <button className="settings-button secondary" type="button" onPointerUp={onClose}>
            {t('back')}
          </button>
          <button
            className="settings-button primary"
            type="button"
            onPointerUp={() => {
              api
                .updateSettings({
                  preferredLanguage: language,
                  preferredAiModel: displayToApi[aiModel],
                })
                .then(() => {
                  const updated = {
                    ...data,
                    user: {
                      ...data.user,
                      preferredLanguage: language,
                      preferredAiModel: displayToApi[aiModel],
                    },
                  }
                  onSave(updated)
                  onClose()
                })
                .catch(err => {
                  const msg = err instanceof Error ? err.message : t('settings_save_failed')
                  setNotifyText(msg)
                })
            }}
          >
            {t('save')}
          </button>
        </div>
      </div>

      {showBuyTokensModal && (
        <BuyTokensModal
          onClose={async () => {
            setShowBuyTokensModal(false)
            const balance = await api.getTokenBalance()
            setTokens(balance)
          }}
          onPremiumChanged={handlePremiumChanged}
          currentPremiumUntil={data.premiumUntil}
        />
      )}
      {notifyText && (
        <NotifyModal text={notifyText} onClose={() => setNotifyText(null)} />
      )}
    </div>
  )
}
